/* ── adminTheme.js — Shared theme tokens for all admin pages ─────────────────
 * Import useAdminTheme() in any admin sub-page to get T (tokens) and helpers.
 * AdminDashboard.jsx injects the CSS variables and DM Sans font.
 * ─────────────────────────────────────────────────────────────────────────── */
import { createContext, useContext } from 'react';

export const AdminThemeContext = createContext({ themeKey: 'light', T: null, toggleTheme: () => {} });

export function useAdminTheme() {
  return useContext(AdminThemeContext);
}

export const ADMIN_THEMES = {
  light: {
    bg:     '#F4F6F9',
    surf:   '#FFFFFF',
    surf2:  '#F0F2F5',
    bdr:    'rgba(0,0,0,0.08)',
    bdr2:   'rgba(0,0,0,0.14)',
    tx:     '#0D0F12',
    tx2:    '#5A6272',
    tx3:    '#9BA3B0',
    brand:  '#1B4FD8',
    brandL: '#EEF2FF',
    brandD: '#0C447C',
    ok:     '#1D9E75',
    okBg:   '#E1F5EE',
    okTx:   '#085041',
    err:    '#DC2626',
    errBg:  '#FEF2F2',
    errTx:  '#991B1B',
    warn:   '#D97706',
    warnBg: '#FFFBEB',
    warnTx: '#92400E',
    sidebar:     '#0f2744',
    sidebarText: 'rgba(255,255,255,0.55)',
    sidebarActive:'rgba(37,99,235,0.25)',
    topbar: '#FFFFFF',
    shadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  dark: {
    bg:     '#0B0F1A',
    surf:   '#141928',
    surf2:  '#1C2236',
    bdr:    'rgba(255,255,255,0.08)',
    bdr2:   'rgba(255,255,255,0.15)',
    tx:     '#F0F4FF',
    tx2:    '#8B95B0',
    tx3:    '#4B5470',
    brand:  '#4F7EF7',
    brandL: '#1A2460',
    brandD: '#93B4FF',
    ok:     '#34D399',
    okBg:   '#052E1C',
    okTx:   '#6EE7B7',
    err:    '#F87171',
    errBg:  '#2D1010',
    errTx:  '#FCA5A5',
    warn:   '#FBBF24',
    warnBg: '#2D1F00',
    warnTx: '#FDE68A',
    sidebar:     '#0B0F1A',
    sidebarText: 'rgba(255,255,255,0.45)',
    sidebarActive:'rgba(79,126,247,0.2)',
    topbar: '#141928',
    shadow: '0 1px 3px rgba(0,0,0,0.4)',
  },
};

/* ── CSS variable injector ────────────────────────────────────────────────── */
export function injectAdminThemeVars(T) {
  const root = document.documentElement;
  root.style.setProperty('--nb-bg',      T.bg);
  root.style.setProperty('--nb-surf',    T.surf);
  root.style.setProperty('--nb-surf2',   T.surf2);
  root.style.setProperty('--nb-bdr',     T.bdr);
  root.style.setProperty('--nb-bdr2',    T.bdr2);
  root.style.setProperty('--nb-tx',      T.tx);
  root.style.setProperty('--nb-tx2',     T.tx2);
  root.style.setProperty('--nb-tx3',     T.tx3);
  root.style.setProperty('--nb-brand',   T.brand);
  root.style.setProperty('--nb-brandL',  T.brandL);
  root.style.setProperty('--nb-ok',      T.ok);
  root.style.setProperty('--nb-okBg',    T.okBg);
  root.style.setProperty('--nb-err',     T.err);
  root.style.setProperty('--nb-errBg',   T.errBg);
  root.style.setProperty('--nb-warn',    T.warn);
  root.style.setProperty('--nb-warnBg',  T.warnBg);
  root.style.setProperty('--nb-warnTx',  T.warnTx);
}

/* ── Shared style helpers for sub-pages ──────────────────────────────────────
 * Call getAdminStyles(T) in any sub-page to get consistent styles.
 * ─────────────────────────────────────────────────────────────────────────── */
export function getAdminStyles(T) {
  return {
    page:      { fontFamily: "'DM Sans',-apple-system,sans-serif", color: T.tx, fontSize: 13 },
    card:      { background: T.surf, border: `0.5px solid ${T.bdr}`, borderRadius: 10, overflow: 'hidden', marginBottom: 16 },
    cardHead:  { padding: '14px 20px', borderBottom: `0.5px solid ${T.bdr}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    cardTitle: { fontSize: 13, fontWeight: 600, color: T.tx },
    cardSub:   { fontSize: 11, color: T.tx2, marginTop: 2 },
    th:        { fontSize: 10, color: T.tx2, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, padding: '9px 16px', background: T.surf2, borderBottom: `0.5px solid ${T.bdr}` },
    td:        { fontSize: 12, color: T.tx, padding: '11px 16px', verticalAlign: 'middle', borderBottom: `0.5px solid ${T.bdr}` },
    input:     { padding: '7px 10px', borderRadius: 6, border: `0.5px solid ${T.bdr2}`, background: T.surf, fontSize: 12, color: T.tx, outline: 'none', fontFamily: "'DM Sans',sans-serif" },
    btn:       { padding: '7px 16px', borderRadius: 6, border: 'none', background: T.brand, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" },
    btnGhost:  { padding: '7px 16px', borderRadius: 6, border: `0.5px solid ${T.bdr2}`, background: 'transparent', color: T.tx2, fontSize: 12, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" },
    statCard:  { background: T.surf, border: `0.5px solid ${T.bdr}`, borderRadius: 10, padding: '14px 16px' },
    statLabel: { fontSize: 10, color: T.tx2, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 },
    statNum:   (col) => ({ fontSize: 24, fontWeight: 600, color: col || T.brand }),
    badge:     (bg, col, bdr) => ({ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: bg, color: col, border: `0.5px solid ${bdr || bg}` }),
    pill:      (bg, col) => ({ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: bg, color: col }),
    empty:     { padding: 48, textAlign: 'center', color: T.tx3, fontSize: 13 },
    toast:     (type) => ({ position: 'fixed', top: 16, right: 16, zIndex: 9999, background: type === 'error' ? T.errBg : T.okBg, border: `0.5px solid ${type === 'error' ? T.err : T.ok}`, color: type === 'error' ? T.err : T.ok, padding: '10px 18px', borderRadius: 8, fontSize: 13, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }),
    tabBtn:    (active) => ({ padding: '9px 18px', cursor: 'pointer', fontSize: 12, fontWeight: 500, color: active ? T.brand : T.tx2, borderBottom: `2px solid ${active ? T.brand : 'transparent'}`, background: active ? T.brandL : 'transparent', border: 'none', fontFamily: "'DM Sans',sans-serif" }),
    row:       (i) => ({ borderBottom: `0.5px solid ${T.bdr}`, background: i % 2 === 0 ? T.surf : T.surf2 }),
  };
}